import request from "@/api/request";

/* ============== PathNavigationSwitch 个性化配置 本地缓存（只拦这 3 个接口，其它接口原样透传） ==============
 * 读 PathNavigationSwitchPageList -> 命中缓存直接返回，未命中才请求（同一 diyName 并发只请求 1 次）
 * 写 AddPathNavigationSwitch      -> 正常请求，成功后直接用“提交的参数”写缓存，不再回读列表
 * 删 DeletePathNavigationSwitch   -> 正常请求，成功后同步剔除缓存记录
 * 只替换 $api 的指向，request.js 不做任何修改；要回滚把 index.js 换回 require("./request") 即可
 * ============================================================================================== */
const PNS_LIST_URL = "/api/PathNavigationSwitch/PathNavigationSwitchPageList";
const PNS_ADD_URL = "/api/PathNavigationSwitch/AddPathNavigationSwitch";
const PNS_DELETE_URL = "/api/PathNavigationSwitch/DeletePathNavigationSwitch";
const PNS_CACHE_VER = "v2"; // 缓存结构有变动时改这里，旧缓存自动作废（v2：旧缓存里可能存了丢精度的雪花 id）
const PNS_CACHE_TTL = 0; // 0=永不过期；想兜底可写 30 * 60 * 1000
const PNS_SYNC_AFTER_ADD = true; // Add 未返回新纪录 id 时，回读 1 次补齐 id

const pnsInflight = {}; // 同一个 diyName 的并发请求去重

// 缓存按 “环境 server + 登录账号 token” 隔离，避免切换账号读到别人的配置
function pnsScopeKey() {
  const token = window.localStorage.getItem("token") || "";
  let hash = 5381;
  for (let i = 0; i < token.length; i++) {
    hash = ((hash << 5) + hash + token.charCodeAt(i)) | 0;
  }
  return (
    "pns:" +
    PNS_CACHE_VER +
    ":" +
    (window.localStorage.getItem("server") || "") +
    ":" +
    (hash >>> 0).toString(36)
  );
}

// 版本号换掉之后，旧版本的缓存不会再被读取（key 里带版本号），启动时直接清掉：
// 一是别一直占着 localStorage，二是防止哪天版本号回退后又把旧数据（可能存过失真 id）读回来
function pnsPurgeStaleKeys() {
  try {
    const prefix = "pns:";
    const current = prefix + PNS_CACHE_VER + ":";
    Object.keys(window.localStorage)
      .filter((key) => key.indexOf(prefix) === 0 && key.indexOf(current) !== 0)
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (e) {
    /* 清理失败不影响业务 */
  }
}

pnsPurgeStaleKeys();

// 调用点传的都是 diyName 字符串，这里兼容传对象的情况
function pnsDiyName(params) {
  if (!params) return "";
  if (typeof params === "string") return params;
  return params.diyName || params.jsonName || "";
}

// 雪花 id 是 19 位长整型，超出 JS 安全整数范围，只能按字符串传递/比较：
// 一旦被 Number()/JSON.parse 转成数字就会变成 399809210774237200 这种失真值，拿它去删/改必然对不上
// 兼容调用方传：裸 id / { pathNavigationSwitchPageId } / { id } / { pageId }
function pnsRawId(source) {
  let value = source;
  if (value && typeof value === "object") {
    const keys = ["pathNavigationSwitchPageId", "id", "pageId"];
    for (let i = 0; i < keys.length; i++) {
      if (value[keys[i]] !== undefined && value[keys[i]] !== null) {
        value = value[keys[i]];
        break;
      }
    }
    // 一个 id 字段都没取到，说明这不是能识别的 id
    if (value === source) return undefined;
  }
  return value;
}

function pnsIdOf(source) {
  const value = pnsRawId(source);
  if (value === undefined || value === null) return "";
  const str = String(value).trim();
  return str === "" || str === "0" || str === "null" || str === "undefined"
    ? ""
    : str;
}

// 数字型的雪花 id 一定已经丢精度（Number 只能精确表示 2^53 以内的整数），不能拿去匹配
function pnsIdTrusted(source) {
  const value = pnsRawId(source);
  if (typeof value === "string") return true;
  return typeof value === "number" && Number.isSafeInteger(value);
}

// 两边都得是可信 id 且完全相等才算同一条，不做“转成数字再比”的降级比较
function pnsSameId(a, b) {
  const ka = pnsIdOf(a);
  const kb = pnsIdOf(b);
  return !!ka && ka === kb;
}

function pnsReadStore() {
  try {
    const all = JSON.parse(window.localStorage.getItem(pnsScopeKey()) || "{}");
    if (PNS_CACHE_TTL > 0) {
      const now = Date.now();
      Object.keys(all).forEach((k) => {
        if (!all[k] || now - (all[k].t || 0) > PNS_CACHE_TTL) delete all[k];
      });
    }
    return all;
  } catch (e) {
    return {};
  }
}

function pnsWriteStore(all) {
  try {
    window.localStorage.setItem(pnsScopeKey(), JSON.stringify(all));
  } catch (e) {
    /* 缓存写失败不影响业务 */
  }
}

// dirty=true 表示本地记录的 id 不可信，下次读必须走接口
function pnsSetCache(diyName, list, dirty) {
  if (!diyName) return;
  const all = pnsReadStore();
  all[diyName] = { t: Date.now(), dirty: !!dirty, list: list || [] };
  pnsWriteStore(all);
}

function pnsGetCache(diyName) {
  const item = pnsReadStore()[diyName];
  if (!item || item.dirty) return null;
  // 缓存里混进了数字型的雪花 id（丢过精度）就整条作废，强制回源拿接口返回的精确 id
  const lossy = (item.list || []).some((row) => {
    const value = pnsRawId(row.pathNavigationSwitchPageId);
    return typeof value === "number" && !Number.isSafeInteger(value);
  });
  return lossy ? null : item.list;
}

// Add 成功后：直接用提交的参数更新缓存，返回 { diyName, needSync }
function pnsUpsertCache(data, res) {
  const diyName = pnsDiyName(data);
  if (!diyName) return { diyName: "", needSync: false };
  const all = pnsReadStore();
  const current = (all[diyName] && all[diyName].list) || [];

  // 后端如果返回了新纪录 id，优先用它
  let resIdRaw;
  if (res && res.data !== undefined && res.data !== null) {
    resIdRaw =
      typeof res.data === "object"
        ? res.data.pathNavigationSwitchPageId
        : res.data;
  }
  const submitId = pnsIdOf(data.pathNavigationSwitchPageId);
  const resId = pnsIdOf(resIdRaw);
  // 提交参数里的 id 优先；数字型（丢过精度的）或拿不到的 id 都不可信，标签 dirty 等下次回源
  let id = "";
  if (submitId && pnsIdTrusted(data.pathNavigationSwitchPageId)) id = submitId;
  else if (resId && pnsIdTrusted(resIdRaw)) id = resId;

  const index = id
    ? current.findIndex((item) =>
      pnsSameId(item.pathNavigationSwitchPageId, id)
    )
    : -1;
  const record = Object.assign({}, index > -1 ? current[index] : {}, data);
  // 原样保留提交/后端返回的 id（字符串），不要转 Number，避免雪花 id 丢精度
  let rawId = 0;
  if (id) rawId = submitId ? data.pathNavigationSwitchPageId : resIdRaw;
  record.pathNavigationSwitchPageId = rawId;
  record.isActive = !!data.isActive;
  if (record.count === undefined) {
    record.count = data.coune === undefined ? 1 : data.coune;
  }

  let list = current.slice();
  if (index > -1) list.splice(index, 1, record);
  else list.push(record);
  // 激活某一条时，其它条置为未激活（与接口返回的列表语义一致）
  if (record.isActive) {
    list = list.map((item) =>
      item === record ? item : Object.assign({}, item, { isActive: false })
    );
  }

  const needSync = !id && PNS_SYNC_AFTER_ADD;
  pnsSetCache(diyName, list, !id); // id 拿不到先标 dirty，回读成功后会清掉
  return { diyName, needSync };
}

// 让所有缓存下次读都回源（id 不可信 / 缓存对不上时用）
function pnsMarkAllDirty(all) {
  let changed = false;
  Object.keys(all).forEach((diyName) => {
    if (all[diyName] && !all[diyName].dirty) {
      all[diyName].dirty = true;
      changed = true;
    }
  });
  return changed;
}

// Delete 成功后按 id 清缓存
// payload 是删除接口的请求参数：裸雪花 id（字符串），也兼容带 id 的对象
function pnsRemoveCache(payload) {
  const all = pnsReadStore();
  const id = pnsIdOf(payload);
  // 删除请求里的 id 是数字型的雪花 id（已经被 Number 丢了精度，如 399809210774237200）时不能拿来匹配，
  // 直接整体标脏，下次读回源拿接口返回的精确 id
  if (!id || !pnsIdTrusted(payload)) {
    if (pnsMarkAllDirty(all)) pnsWriteStore(all);
    return;
  }
  let removed = false;
  Object.keys(all).forEach((diyName) => {
    const item = all[diyName];
    if (!item || !item.list) return;
    const list = item.list.filter(
      (row) => !pnsSameId(row.pathNavigationSwitchPageId, id)
    );
    if (list.length !== item.list.length) {
      all[diyName] = { t: Date.now(), dirty: item.dirty, list };
      removed = true;
    }
  });
  // 一条都没删掉说明缓存和这次删除对不上，整体标脏回源，避免删掉的记录还留在缓存里
  if (!removed) pnsMarkAllDirty(all);
  pnsWriteStore(all);
}

// 读接口：缓存优先 + 并发去重
function pnsListRequest(params, doRequest) {
  const diyName = pnsDiyName(params);
  if (!diyName) return doRequest();

  const cached = pnsGetCache(diyName);
  if (cached) {
    // 返回结构和接口保持一致，业务代码不用改
    return Promise.resolve({
      code: 200,
      data: cached,
      msg: "",
      fromCache: true,
    });
  }
  if (pnsInflight[diyName]) return pnsInflight[diyName];

  const task = doRequest()
    .then((res) => {
      if (res && Array.isArray(res.data)) pnsSetCache(diyName, res.data);
      delete pnsInflight[diyName];
      return res;
    })
    .catch((err) => {
      delete pnsInflight[diyName];
      throw err;
    });
  pnsInflight[diyName] = task;
  return task;
}

// $api 的对外结构保持和 request.js 一致
export default {
  ...request,
  get: request.get,
  upload: request.upload,
  post(
    url,
    params,
    options,
    header,
    autoMessage,
    passFrequent,
    passErrorMessage,
    noTimeout
  ) {
    const doRequest = () =>
      request.post(
        url,
        params,
        options,
        header,
        autoMessage,
        passFrequent,
        passErrorMessage,
        noTimeout
      );

    // ① 读：有缓存直接返回，不走接口
    if (url === PNS_LIST_URL) return pnsListRequest(params, doRequest);

    // ② 新增/修改：请求成功后直接用提交的参数刷新缓存
    if (url === PNS_ADD_URL) {
      return doRequest().then((res) => {
        const { diyName, needSync } = pnsUpsertCache(params, res);
        if (!needSync || !diyName) return res;
        // 新纪录拿不到真实 id：只回读 1 次补齐 id（避免下次保存 id=0 又插一条）
        return request
          .post(PNS_LIST_URL, diyName, { loading: false })
          .then((listRes) => {
            pnsSetCache(diyName, listRes.data || []);
            return res;
          })
          .catch(() => res); // 回读失败就保持 dirty，下次读自动走接口
      });
    }

    // ③ 删除：请求成功后同步剔除缓存
    if (url === PNS_DELETE_URL) {
      return doRequest().then((res) => {
        pnsRemoveCache(params);
        return res;
      });
    }

    return doRequest();
  },
};
